import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import InrixVolumeUrlKeys from "../../../../../../constant/InrixVolumeUrlKeys";

export enum InrixVolumeTimePeriod {
  ADT = "adt",
  FIFTEEN_MINS = "15mins",
}

export enum InrixVolumeDayOfWeek {
  SUNDAY = "sunday",
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
}

const isInrixVolumeTimePeriod = (
  value: string | null,
): value is InrixVolumeTimePeriod =>
  value === InrixVolumeTimePeriod.ADT ||
  value === InrixVolumeTimePeriod.FIFTEEN_MINS;

const isInrixVolumeDayOfWeek = (
  value: string | null,
): value is InrixVolumeDayOfWeek =>
  value === InrixVolumeDayOfWeek.SUNDAY ||
  value === InrixVolumeDayOfWeek.MONDAY ||
  value === InrixVolumeDayOfWeek.TUESDAY ||
  value === InrixVolumeDayOfWeek.WEDNESDAY ||
  value === InrixVolumeDayOfWeek.THURSDAY ||
  value === InrixVolumeDayOfWeek.FRIDAY ||
  value === InrixVolumeDayOfWeek.SATURDAY;

const dayOrder = [
  InrixVolumeDayOfWeek.SUNDAY,
  InrixVolumeDayOfWeek.MONDAY,
  InrixVolumeDayOfWeek.TUESDAY,
  InrixVolumeDayOfWeek.WEDNESDAY,
  InrixVolumeDayOfWeek.THURSDAY,
  InrixVolumeDayOfWeek.FRIDAY,
  InrixVolumeDayOfWeek.SATURDAY,
] as const;

const DEFAULT_FIFTEEN_MINS_DAY = InrixVolumeDayOfWeek.SUNDAY;
const DEFAULT_FIFTEEN_MINS_SEGMENT = 0;

const isValidSegment = (value: number | null) =>
  value !== null && !Number.isNaN(value) && value >= 0 && value <= 95;

const isValidTimeIndex = (value: number | null) =>
  value !== null && !Number.isNaN(value) && value >= 0 && value <= 671;

const useGetOrderItemInrixVolumeParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTimePeriod = searchParams.get(InrixVolumeUrlKeys.TIME_PERIOD);
  const timePeriod =
    isInrixVolumeTimePeriod(selectedTimePeriod) ? selectedTimePeriod : (
      InrixVolumeTimePeriod.ADT
    );

  const selectedDayOfWeek = searchParams.get(InrixVolumeUrlKeys.DAY_OF_WEEK);
  const dayOfWeek =
    isInrixVolumeDayOfWeek(selectedDayOfWeek) ? selectedDayOfWeek : null;

  const selectedTimeSegment = searchParams.get(InrixVolumeUrlKeys.TIME_SEGMENT);
  const timeSegmentCandidate =
    selectedTimeSegment === null || selectedTimeSegment === "" ?
      null
    : Number(selectedTimeSegment);
  const timeSegment =
    isValidSegment(timeSegmentCandidate) ? timeSegmentCandidate : null;

  const selectedTimeIndex = searchParams.get(InrixVolumeUrlKeys.TIME_INDEX);
  const timeIndexCandidate =
    selectedTimeIndex === null || selectedTimeIndex === "" ?
      null
    : Number(selectedTimeIndex);
  const timeIndexFromUrl =
    isValidTimeIndex(timeIndexCandidate) ? timeIndexCandidate : null;

  const derivedTimeIndex =
    dayOfWeek !== null && timeSegment !== null ?
      dayOrder.indexOf(dayOfWeek) * 96 + timeSegment
    : null;

  const timeIndex = timeIndexFromUrl ?? derivedTimeIndex;

  const resolvedDayOfWeek =
    timeIndex !== null ? dayOrder[Math.floor(timeIndex / 96)] : dayOfWeek;
  const resolvedTimeSegment = timeIndex !== null ? timeIndex % 96 : timeSegment;

  const handleSelectTimePeriod = useCallback(
    (value: InrixVolumeTimePeriod) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(InrixVolumeUrlKeys.TIME_PERIOD, value);
        if (value === InrixVolumeTimePeriod.ADT) {
          newParams.delete(InrixVolumeUrlKeys.DAY_OF_WEEK);
          newParams.delete(InrixVolumeUrlKeys.TIME_SEGMENT);
          newParams.delete(InrixVolumeUrlKeys.TIME_INDEX);
        } else {
          newParams.set(
            InrixVolumeUrlKeys.DAY_OF_WEEK,
            DEFAULT_FIFTEEN_MINS_DAY,
          );
          newParams.set(
            InrixVolumeUrlKeys.TIME_SEGMENT,
            String(DEFAULT_FIFTEEN_MINS_SEGMENT),
          );
          newParams.set(
            InrixVolumeUrlKeys.TIME_INDEX,
            String(
              dayOrder.indexOf(DEFAULT_FIFTEEN_MINS_DAY) * 96 +
                DEFAULT_FIFTEEN_MINS_SEGMENT,
            ),
          );
        }
        return newParams;
      });
    },
    [setSearchParams],
  );

  const handleSelectDayOfWeek = useCallback(
    (value: InrixVolumeDayOfWeek) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(InrixVolumeUrlKeys.DAY_OF_WEEK, value);
        if (resolvedTimeSegment !== null) {
          newParams.set(
            InrixVolumeUrlKeys.TIME_SEGMENT,
            String(resolvedTimeSegment),
          );
          newParams.set(
            InrixVolumeUrlKeys.TIME_INDEX,
            String(dayOrder.indexOf(value) * 96 + resolvedTimeSegment),
          );
        } else {
          newParams.delete(InrixVolumeUrlKeys.TIME_INDEX);
        }
        return newParams;
      });
    },
    [resolvedTimeSegment, setSearchParams],
  );

  const handleSelectTimeSegment = useCallback(
    (value: number) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set(InrixVolumeUrlKeys.TIME_SEGMENT, String(value));
        if (resolvedDayOfWeek !== null) {
          newParams.set(InrixVolumeUrlKeys.DAY_OF_WEEK, resolvedDayOfWeek);
          newParams.set(
            InrixVolumeUrlKeys.TIME_INDEX,
            String(dayOrder.indexOf(resolvedDayOfWeek) * 96 + value),
          );
        } else {
          newParams.delete(InrixVolumeUrlKeys.TIME_INDEX);
        }
        return newParams;
      });
    },
    [resolvedDayOfWeek, setSearchParams],
  );

  return {
    timePeriod,
    dayOfWeekInUrl: dayOfWeek,
    dayOfWeek: resolvedDayOfWeek,
    timeSegment: resolvedTimeSegment,
    timeIndex,
    handleSelectTimePeriod,
    handleSelectDayOfWeek,
    handleSelectTimeSegment,
  };
};

export default useGetOrderItemInrixVolumeParams;
